import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiBox, FiDroplet, FiFileText, FiPackage, FiScissors, FiSliders, FiTruck, FiWind } from "react-icons/fi";
import ContactModal from "../ContactModal/ContactModal";
import Seo, { breadcrumbSchema, privateLabelServiceSchema, webPageSchema } from "../Seo/Seo";
import formulaTesting from "../../Assets/blogs/laundry-detergent-sheet-formula-testing.webp";
import mixing from "../../Assets/production/formulation-mixing.webp";
import mixingSmall from "../../Assets/production/formulation-mixing-800.webp";
import formation from "../../Assets/production/sheet-formation.webp";
import formationSmall from "../../Assets/production/sheet-formation-800.webp";
import cutting from "../../Assets/production/cutting-perforation.webp";
import cuttingSmall from "../../Assets/production/cutting-perforation-800.webp";
import packaging from "../../Assets/production/private-label-packaging.webp";
import packagingSmall from "../../Assets/production/private-label-packaging-800.webp";

const collectionPath = "/products?category=sheets";
const packagingPath = "/products/laundry-detergent-sheets#product-laundry-detergent-sheets-detail-packaging";
const pageTitle = "Laundry Detergent Sheet Manufacturing Process | Klean Leaf";
const pageDescription = "See how Klean Leaf manufactures custom laundry detergent sheets, from formulation and sheet formation to cutting, perforation and private label packaging.";
const steps = [
  {
    id: "formulation", title: "Custom Formulation and Mixing", Icon: FiDroplet,
    copy: "We blend your formula for scent or fragrance-free needs, performance goals and your target market.",
    image: mixing, smallImage: mixingSmall,
    alt: "Stainless steel mixing tanks used to blend laundry detergent sheet ingredients at Klean Leaf",
    links: [
      { label: "3-in-1 enzyme sheets", to: "/products/3-in-1-enzyme-laundry-detergent-sheets" },
      { label: "PVA-free sheets", to: "/products/pva-free-laundry-detergent-sheets" },
    ],
  },
  {
    id: "sheet-formation", title: "Evaporation and Sheet Formation", Icon: FiWind,
    copy: "Controlled forming and drying turn blended detergent into compact sheet material, ready for cutting.",
    image: formation, smallImage: formationSmall,
    alt: "Klean Leaf detergent sheet manufacturing line with wide rolls of formed sheet material",
  },
  {
    id: "cutting", title: "Cutting and Perforation", Icon: FiScissors,
    copy: "Cutting and optional perforation prepare full or half sheets in your chosen size and sheet count.",
    image: cutting, smallImage: cuttingSmall,
    alt: "White laundry detergent sheets on a conveyor at the cutting and sizing stage",
  },
  {
    id: "packaging", title: "Private Label Packaging", Icon: FiPackage,
    copy: "Sheets go into branded boxes, pouches or bulk packs, with artwork coordination for retail-ready packaging.",
    image: packaging, smallImage: packagingSmall,
    alt: "Production staff packing finished laundry detergent sheets into private label boxes on a packaging line",
    links: [{ label: "Explore packaging options", to: packagingPath }],
  },
];
const support = [
  { Icon: FiSliders, title: "Custom Formula Support", copy: "Review scent, sheet feel and performance needs." },
  { Icon: FiBox, title: "Packaging Coordination", copy: "Dielines, artwork and samples for your brand." },
  { Icon: FiFileText, title: "Documentation Available", copy: "Request documents for your formula and market." },
  { Icon: FiTruck, title: "Global Shipping Support", copy: "Carton planning, freight and delivery coordination." },
];

function Production() {
  const [inquiryOpen, setInquiryOpen] = useState(false);

  return (
    <>
      <Seo title={pageTitle} description={pageDescription} path="/production"
        image={formulaTesting} imageAlt="Klean Leaf laboratory team testing laundry detergent sheet formulas"
        imageType="image/webp" imageWidth={1200} imageHeight={900}
        schema={[
          privateLabelServiceSchema,
          webPageSchema({ name: pageTitle, description: pageDescription, path: "/production", primaryImage: formulaTesting }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Production", path: "/production" }]),
        ]} />
      <main className="production-page mobile-type">
        <section className="production-hero" aria-labelledby="production-title">
          <div className="production-container production-hero-layout">
            <div className="production-hero-copy">
              <p className="production-eyebrow">Production process</p>
              <h1 id="production-title">How We Make<br /><span>Laundry Detergent Sheets</span></h1>
              <p className="production-lead">From formulation and sheet formation to cutting and private label packaging, we manufacture custom laundry detergent sheets for your brand.</p>
              <div className="production-actions">
                <Link to={collectionPath} className="production-button">Explore Laundry Detergent Sheets</Link>
                <button type="button" className="production-text-link" onClick={() => setInquiryOpen(true)}>Start Your Project</button>
              </div>
            </div>
            <figure className="production-hero-visual">
              <img src={formulaTesting} alt="Klean Leaf laboratory team testing laundry detergent sheet formulas" width={1200} height={900} decoding="async" />
              <figcaption>Inside our formula testing lab <span>China</span></figcaption>
            </figure>
          </div>
        </section>

        <section className="production-process production-container" aria-labelledby="production-process-title">
          <header className="production-process-intro">
            <div>
              <p className="production-eyebrow">Laundry detergent sheet production</p>
              <h2 id="production-process-title">From formula to<br />finished product.</h2>
            </div>
            <p>Klean Leaf is a private label laundry detergent sheets manufacturer. These four stages take your formula through detergent sheet manufacturing to a finished pack for your brand.</p>
          </header>
          <ol className="production-timeline" aria-label="Laundry detergent sheet production in four steps">
            {steps.map(({ id, title, Icon, copy, image, smallImage, alt, links }, index) => (
              <li className="production-step" id={`production-${id}`} key={id}>
                <span className="production-step-number" aria-hidden="true">0{index + 1}</span>
                <article className="production-step-content" aria-labelledby={`production-${id}-title`}>
                  <div className="production-step-copy">
                    <Icon className="production-step-icon" aria-hidden="true" />
                    <h3 id={`production-${id}-title`}><span className="visually-hidden">Step {index + 1}: </span>{title}</h3>
                    <p>{copy}</p>
                    {links && <div className="production-step-links">
                      {links.map(({ label, to }) => <Link key={to} to={to}>{label}</Link>)}
                    </div>}
                  </div>
                  <div className="production-step-photo">
                    <img src={image} srcSet={`${smallImage} 800w, ${image} 1600w`}
                      sizes="(max-width: 767px) calc(100vw - 88px), (max-width: 991px) 52vw, (max-width: 1376px) 48vw, 606px"
                      alt={alt} width={1600} height={1600} loading="lazy" decoding="async" />
                  </div>
                </article>
              </li>
            ))}
          </ol>
        </section>

        <section className="production-support" aria-labelledby="production-support-title">
          <div className="production-container">
            <h2 id="production-support-title">Production Support for Private Label Brands</h2>
            <ul className="production-support-list">
              {support.map(({ Icon, title, copy }) => <li key={title}>
                <Icon aria-hidden="true" />
                <h3>{title}</h3>
                <p>{copy}</p>
              </li>)}
            </ul>
          </div>
        </section>

        <section className="production-final-cta" aria-labelledby="production-cta-title">
          <div className="production-container production-cta-layout">
            <div>
              <h2 id="production-cta-title">Ready to Build Your<br />Laundry Sheet Product?</h2>
              <p>Explore product options or talk with our team about formula, packaging and production requirements.</p>
            </div>
            <div className="production-actions">
              <button type="button" className="production-button" onClick={() => setInquiryOpen(true)}>Request Samples or Quote <FiArrowRight aria-hidden="true" /></button>
              <Link to={collectionPath} className="production-text-link">Explore Laundry Detergent Sheets</Link>
            </div>
          </div>
        </section>
      </main>
      <ContactModal mobileTypography isModalOpen={inquiryOpen} handleCancel={() => setInquiryOpen(false)}
        idPrefix="production-project-contact" title="Start Your Laundry Sheet Project" productName="Laundry Detergent Sheets" />
    </>
  );
}

export default Production;
